import { useRef, useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  HelpCircle,
  LogOut,
  Menu,
  User,
} from "lucide-react";
import { useAuthStore } from "../hooks/useAuthStore";
import { useAppConfigContext } from "../context/AppConfigContext";

const profileActions = [
  { label: "Profile", to: "/assistant/profile", Icon: User },
  { label: "Help Center", to: "/help-center", Icon: HelpCircle },
];

function HeaderAssistant() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleButtonRef = useRef(null);

  const { user, userName, actions } = useAuthStore();
  const { config } = useAppConfigContext();

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenu);
    return () => {
      document.removeEventListener("mousedown", closeMenu);
    };
  }, []);

  const getUserEmail = () =>
    user?.email || "";

  const getUserAvatar = () => {
    if (!user) return "";
    return (
      user.avatar ||
      (typeof user.profilePicture === "string" ? user.profilePicture : user.profilePicture?.url) ||
      user.photo ||
      user.image ||
      ""
    );
  };

  const handleLogout = async () => {
    await actions.logout();
    setIsMenuOpen(false);
    navigate("/auth/login", { replace: true });
  };

  const goTo = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (!parts.length) return "A";
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "A";
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const displayName = userName || "Assistant";
  const avatar = getUserAvatar();
  const email = getUserEmail();

  return (
    <header className="sticky inset-x-0 top-0 z-50 bg-white/95 backdrop-blur">
      <div className="flex w-full items-center justify-between px-4 py-3 sm:px-6">
        <NavLink to="/dashboards/assistant/overview" className="flex items-center gap-2">
          <img
            src={config.logoUrl || "/images/logo.png"}
            alt={config.appName || "Pulo logo"}
            className="h-10 w-auto"
          />
        </NavLink>

        <div className="relative flex items-center gap-3">
          <button
            ref={toggleButtonRef}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="flex items-center gap-3 rounded-full p-2 bg-[#FAF7FA] text-sm font-semibold text-[#61013B] shadow-sm transition hover:border-[#97025C] hover:text-[#97025C]"
            aria-label="Open profile menu"
          >
            <Menu className="h-6 w-6 rounded" />
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 top-[calc(100%+0.75rem)] z-40 w-[320px] max-w-[90vw]">
              <div
                ref={menuRef}
                className="rounded-2xl border border-gray-100 bg-white p-4 shadow-xl shadow-[#97025C]/10"
              >
                <div className="flex flex-col items-center gap-1 pb-4 text-center">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-[#f8e9f3] text-[#97025C] shadow-sm">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={displayName}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "";
                        }}
                      />
                    ) : (
                      <span className="text-lg font-semibold">
                        {getInitials(displayName)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                  {email ? (
                    <p className="text-xs font-medium text-gray-500">{email}</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <div className="divide-y rounded-xl border border-gray-100 bg-gray-50/60">
                    {profileActions.map(({ label, to, Icon }) => (
                      <button
                        key={to}
                        type="button"
                        onClick={() => goTo(to)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-white"
                      >
                        <Icon className="h-4 w-4 text-[#97025C]" />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#6a003f] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#570033]"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default HeaderAssistant;
